import React, { Fragment, useCallback } from 'react';
import { styled, SxProp } from 'dripsy';
import { Observer, observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import {
  BaseText,
  CommentInput,
  CommentInputModeEnum,
  CommentInputSizeEnum,
  CommentList,
  Line,
  Loader,
  Separator,
} from '@components';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import { parseApiError } from '@utils';
import { ToastService } from '@services';
import { useAsyncInit } from '@hooks';
import { User } from '@models/Viewer/UserModel';
import { MainStackParamListType, screens } from '@navigation';

const contentContainerStyle: SxProp = {
  px: ['$6', '$7', '$0'],
};

const CommentsCount = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

const LoaderContainer = styled(View)({
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
});

export interface ICommentsListProps {
  tutorial: Tutorial;
  HeaderComponent: React.ElementType;
}

type NavigationPropType =
  NativeStackNavigationProp<MainStackParamListType>;

export const CommentsList = observer<ICommentsListProps>(
  ({ tutorial, HeaderComponent, ...props }) => {
    const { t } = useTranslation();
    const nav = useNavigation<NavigationPropType>();
    const list = tutorial.comments;

    useAsyncInit(() => fetch());

    async function fetch() {
      try {
        await list.fetch.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    async function fetchMore() {
      try {
        await list.fetchMore.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    const onNavigateToUser = useCallback((user: User) => {
      nav.push(screens.UserProfileTabNavigator, {
        id: user.id,
      });
    }, []);

    const ListHeaderComponent = useCallback(
      () => (
        <Observer>
          {() => {
            const commentsCount = t('tutorial.comment', {
              count: tutorial.comments.totalCount,
            });

            return (
              <Fragment>
                <HeaderComponent />
                <Line />
                <Separator size="$4" />
                <CommentsCount>{commentsCount}</CommentsCount>
                <Separator size="$3" />
                <CommentInput
                  target={{
                    entity: tutorial.comments,
                    mode: CommentInputModeEnum.Create,
                  }}
                  size={CommentInputSizeEnum.Large}
                />
                <Separator size="$8" />
              </Fragment>
            );
          }}
        </Observer>
      ),
      [HeaderComponent],
    );

    return (
      <CommentList
        {...props}
        commentInputSize={CommentInputSizeEnum.Large}
        ListHeaderComponent={ListHeaderComponent}
        data={list.asArray}
        contentContainerSx={contentContainerStyle}
        isLoadingMore={list.fetchMore.inProgress}
        onEndReached={fetchMore}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <Observer>
            {() =>
              list.fetch.inProgress ? (
                <LoaderContainer>
                  <Loader size="large" />
                </LoaderContainer>
              ) : null
            }
          </Observer>
        )}
        onPressUser={onNavigateToUser}
      />
    );
  },
);
